/**
 * ModalEmbalagemCompra — os campos da COMPRA do cadastro do produto, editados de dentro da cotacao
 * (pedido do Ruan, 28/10/2026): Unidade de Compra, Qtd na Emb e UN do conteudo. Sao os mesmos tres
 * campos da ficha, com a mesma lista de unidades — e so eles (decisao dele).
 *
 * Grava pelo `PATCH /api/produtos/:id/embalagem-compra`, que escreve SO estes tres campos: o `PUT`
 * da ficha manda o produto inteiro e apagaria o que outra pessoa mudou noutro campo.
 *
 * ⚠️ A caneta e' a MESMA da ficha ([DA-806]): ao abrir, a janelinha pega a caneta do produto; se
 * outra pessoa estiver com a ficha (ou com esta janelinha) aberta, abre em somente leitura. Mesmo
 * desvio da ficha: aba que vai pro fundo sem nada alterado devolve a caneta e tenta pegar de volta
 * ao reaparecer — senao uma aba esquecida seguraria o produto pro colega.
 */
import { useEffect, useRef, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { formatQtd, parseQtd } from '@fiodecobre/shared';
import { api, ApiError, getToken } from '../../lib/api';
import { Modal } from '../../components/Modal';
import { ComboBox } from '../../components/ComboBox';
import { ModalCadastroRapido } from '../../components/ModalCadastroRapido';

interface OpUnidade { id: number; sigla: string; nome: string; ativo?: boolean }
// Mesma regra da ficha: unidade inativa sai da lista, mas a que o produto JA usa fica, marcada.
function unidadesOfertadas(lista: OpUnidade[], atual: string) {
  return lista.filter((u) => u.ativo !== false || u.sigla === atual);
}
const rotuloUnidade = (u: OpUnidade) => `${u.sigla} — ${u.nome}${u.ativo === false ? ' (inativa)' : ''}`;

export interface EmbalagemCompraGravada {
  unidade_compra: string | null;
  qtd_embalagem_padrao: string | null;
  unidade_conteudo_embalagem: string | null;
}

export function ModalEmbalagemCompra(props: {
  produtoId: number;
  codigo: string | null;
  descricao: string | null;
  onFechar: () => void;
  onGravado: (g: EmbalagemCompraGravada) => void;
}) {
  const { produtoId, codigo, descricao, onFechar, onGravado } = props;

  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [unidades, setUnidades] = useState<OpUnidade[]>([]);
  const [unidadeCompra, setUnidadeCompra] = useState('');
  const [qtd, setQtd] = useState('');
  const [conteudo, setConteudo] = useState('');
  const original = useRef('');
  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [novaUnidade, setNovaUnidade] = useState(false);
  // Sequencia de Tab/Enter pedida por ele: Unidade de Compra -> Qtd -> UN -> Salvar (o + fica fora).
  const qtdRef = useRef<HTMLInputElement>(null);
  const conteudoRef = useRef<HTMLSelectElement>(null);
  const salvarRef = useRef<HTMLButtonElement>(null);

  const alterado = !carregando && JSON.stringify([unidadeCompra, qtd, conteudo]) !== original.current;
  const alteradoRef = useRef(false);
  alteradoRef.current = alterado;

  // --- Caneta (a mesma da ficha, [DA-806]) --------------------------------------------------------
  //   'aguardando' — ainda pedindo; 'minha' — seguro e renovo; 'solta' — devolvi com a aba no fundo;
  //   'deOutro' — somente leitura, nunca bate nem solta (soltar derrubaria a trava de quem edita).
  const canetaRef = useRef<'aguardando' | 'minha' | 'solta' | 'deOutro'>('aguardando');
  const [edicaoDeOutro, setEdicaoDeOutro] = useState<string | null>(null);
  const somenteLeitura = edicaoDeOutro !== null;

  function entrarEmSomenteLeitura(dono: string) {
    canetaRef.current = 'deOutro';
    setEdicaoDeOutro(dono);
  }

  // Rede fora NAO tranca a janelinha: a rota tem a trava real, no maximo o Salvar e' recusado.
  async function pedirCaneta(): Promise<string | null> {
    try {
      const r = await api.post<{ ok: boolean; por_nome?: string }>(`/api/produtos/${produtoId}/editando`);
      if (r.ok) {
        canetaRef.current = 'minha';
        return null;
      }
      return r.por_nome || 'outro usuario';
    } catch {
      canetaRef.current = 'minha';
      return null;
    }
  }

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [p, us, dono] = await Promise.all([
          api.get<any>(`/api/produtos/${produtoId}`),
          api.get<OpUnidade[]>('/api/unidades-medida'),
          pedirCaneta(),
        ]);
        if (!vivo) return;
        setUnidades(us || []);
        const uc = p.unidade_compra ?? '';
        const q = p.qtd_embalagem_padrao != null && Number(p.qtd_embalagem_padrao) > 0 ? formatQtd(p.qtd_embalagem_padrao) : '';
        // Mesmo default da ficha: sem UN do conteudo gravada, a tela mostra a unidade de venda.
        const ct = p.unidade_conteudo_embalagem ?? p.unidade_sigla ?? '';
        // Sem Unidade de Compra gravada, abre com UN (pedido dele). O `original` guarda o vazio de
        // proposito: o UN ainda nao esta no cadastro, entao o Salvar ja nasce aceso.
        setUnidadeCompra(uc || (dono ? '' : 'UN'));
        setQtd(q);
        setConteudo(ct);
        original.current = JSON.stringify([uc, q, ct]);
        if (dono) entrarEmSomenteLeitura(dono);
      } catch (e) {
        if (vivo) setErroCarga(e instanceof ApiError ? e.message : 'Nao consegui abrir o cadastro do produto.');
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produtoId]);

  // Renova a cada 30s; devolve com a aba no fundo sem alteracao, ao fechar e ao fechar a aba.
  useEffect(() => {
    const devolver = () => {
      if (canetaRef.current !== 'minha') return;
      canetaRef.current = 'solta';
      // `keepalive` faz o pedido sobreviver ao fechamento da aba (mesma receita da ficha).
      fetch(`/api/produtos/${produtoId}/editando`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
        keepalive: true,
      }).catch(() => {});
    };
    const perder = (dono: string) => {
      if (canetaRef.current === 'deOutro') return;
      entrarEmSomenteLeitura(dono);
    };
    const bater = () => {
      const visivel = document.visibilityState === 'visible';
      if (canetaRef.current === 'minha') {
        if (!visivel && !alteradoRef.current) { devolver(); return; }
        api.post<{ ok: boolean; por_nome?: string }>(`/api/produtos/${produtoId}/editando`)
          .then((r) => { if (r && !r.ok) perder(r.por_nome || 'outro usuario'); })
          .catch(() => {});
      } else if (canetaRef.current === 'solta' && visivel) {
        void pedirCaneta().then((dono) => { if (dono) perder(dono); });
      }
    };
    const tick = setInterval(bater, 30_000);
    document.addEventListener('visibilitychange', bater);
    window.addEventListener('pagehide', devolver);
    return () => {
      clearInterval(tick);
      document.removeEventListener('visibilitychange', bater);
      window.removeEventListener('pagehide', devolver);
      devolver();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produtoId]);

  async function salvar() {
    if (somenteLeitura || salvando || !alterado) return;
    setSalvando(true);
    setErroSalvar(null);
    try {
      const n = parseQtd(qtd);
      const g = await api.patch<EmbalagemCompraGravada>(`/api/produtos/${produtoId}/embalagem-compra`, {
        unidade_compra: unidadeCompra || null,
        qtd_embalagem_padrao: Number.isFinite(n) && n > 0 ? n : null,
        unidade_conteudo_embalagem: conteudo || null,
      });
      onGravado(g);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.corpo?.codigo === 'edicao_de_outro') {
        entrarEmSomenteLeitura(e.corpo?.por_nome || 'outro usuario');
        setErroSalvar('Nada foi salvo.');
      } else {
        setErroSalvar(e instanceof ApiError ? e.message : 'Erro ao salvar.');
      }
    } finally {
      setSalvando(false);
    }
  }

  const motivoSemSalvar = carregando
    ? null
    : somenteLeitura
      ? 'Somente leitura.'
      : !alterado
        ? 'Nenhuma alteracao pra salvar.'
        : null;

  return (
    <>
      <Modal
        aberto
        onFechar={onFechar}
        titulo="Unidade de Compra"
        largura={560}
        compacto
        rodape={
          <div className="flex items-center justify-end gap-2 w-full">
            {erroSalvar && <span className="text-sm mr-auto" style={{ color: 'var(--danger, #b91c1c)', fontWeight: 600 }}>{erroSalvar}</span>}
            {!erroSalvar && motivoSemSalvar && <span className="text-sm mr-auto" style={{ color: 'var(--text-muted)' }}>{motivoSemSalvar}</span>}
            <button type="button" className="btn-secundario" onClick={onFechar}>
              {alterado && !somenteLeitura ? 'Cancelar' : 'Fechar'}
            </button>
            <button
              ref={salvarRef}
              type="button"
              className="btn-primario inline-flex items-center gap-1"
              onClick={() => void salvar()}
              disabled={!!motivoSemSalvar || carregando || salvando || !!erroCarga}
            >
              {salvando && <Loader2 size={14} className="animate-spin" />}
              Salvar
            </button>
          </div>
        }
      >
        <div className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
          <span style={{ fontWeight: 700, color: 'var(--text)' }}>{codigo || '—'}</span>
          {' · '}
          {descricao || ''}
        </div>

        {edicaoDeOutro && (
          <div className="mb-3 px-3 py-2 rounded-lg text-sm" style={{ background: '#fde68a', color: '#000', fontWeight: 600 }}>
            {edicaoDeOutro} esta editando este produto — somente leitura. Quando {edicaoDeOutro} sair, feche e abra de novo para editar.
          </div>
        )}

        {carregando ? (
          <div className="flex items-center gap-2 text-sm py-4" style={{ color: 'var(--text-muted)' }}>
            <Loader2 size={16} className="animate-spin" /> Carregando o cadastro…
          </div>
        ) : erroCarga ? (
          <div className="text-sm py-2" style={{ color: 'var(--danger, #b91c1c)', fontWeight: 600 }}>{erroCarga}</div>
        ) : (
          <fieldset disabled={somenteLeitura || salvando} style={{ border: 0, padding: 0, margin: 0 }}>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
              Unidade de Compra <span className="font-normal" style={{ color: 'var(--text-muted)' }}>(+ Qtd na Emb + UN do conteudo)</span>
            </label>
            <div className="flex gap-1">
              <div className="flex-1 min-w-0">
                <ComboBox
                  opcoes={unidadesOfertadas(unidades, unidadeCompra).map((u) => ({ value: u.sigla, label: rotuloUnidade(u) }))}
                  value={unidadeCompra}
                  onChange={(v) => setUnidadeCompra(String(v))}
                  placeholder="Selecione..."
                  autoFocus={!somenteLeitura}
                  aoAvancar={() => qtdRef.current?.focus()}
                />
              </div>
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setNovaUnidade(true)}
                className="px-2 py-1 bg-gray-100 hover:bg-blue-50 hover:text-blue-600 text-gray-500 rounded-lg transition flex-shrink-0"
                title="Nova unidade"
              >
                <Plus size={16} />
              </button>
              <input
                ref={qtdRef}
                type="text"
                inputMode="decimal"
                name="qtd_embalagem_padrao"
                value={qtd}
                onChange={(e) => setQtd(e.target.value)}
                onBlur={(e) => {
                  const n = parseQtd(e.target.value);
                  setQtd(Number.isFinite(n) && n > 0 ? formatQtd(n) : '');
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); conteudoRef.current?.focus(); } }}
                placeholder="Qtd"
                className="cel-num w-24 px-2 py-1.5 border border-gray-300 rounded-lg text-sm flex-shrink-0"
                title="Quantidade por embalagem (ex: 1600 metros por bobina)."
              />
              <select
                ref={conteudoRef}
                value={conteudo}
                onChange={(e) => setConteudo(e.target.value)}
                // Pula o Fechar e vai direto ao Salvar. Salvar apagado (nada mudou) nao recebe foco:
                // o Tab segue o caminho normal e o Enter fica parado.
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) && salvarRef.current && !salvarRef.current.disabled) {
                    e.preventDefault();
                    salvarRef.current.focus();
                  }
                }}
                className="w-24 px-2 py-1.5 border border-gray-300 rounded-lg text-sm flex-shrink-0 bg-white"
                title="Unidade do conteudo da embalagem (ex: BB com 1600 M)."
              >
                <option value="">UN…</option>
                {unidadesOfertadas(unidades, conteudo).map((u) => <option key={u.id} value={u.sigla}>{u.sigla}</option>)}
              </select>
            </div>
          </fieldset>
        )}
      </Modal>

      {novaUnidade && (
        <ModalCadastroRapido
          titulo="Nova Unidade"
          endpoint="/api/unidades-medida"
          campos={[
            { key: 'sigla', label: 'Sigla', obrigatorio: true },
            { key: 'nome', label: 'Nome', obrigatorio: true },
            { key: 'permite_fracionado', label: 'Permite fracionado', tipo: 'boolean', descricao: 'Permite venda fracionada' },
          ]}
          onSalvo={(novo: OpUnidade) => {
            setUnidades((p) => [...p, novo]);
            setUnidadeCompra(novo.sigla);
            setNovaUnidade(false);
          }}
          onFechar={() => setNovaUnidade(false)}
        />
      )}
    </>
  );
}
